import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { PRODUCT } from '../data/product';

const CartContext = createContext(null);
const STORAGE_KEY = 'rj_shop_cart_v1';

function loadStoredCart() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_error) {
    return [];
  }
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => loadStoredCart());

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const actions = useMemo(
    () => ({
      items,
      addItem({ colorName, image, quantity = 1 }) {
        setItems((prev) => {
          const id = `${PRODUCT.id}-${colorName.toLowerCase()}`;
          const existing = prev.find((item) => item.id === id);

          if (existing) {
            return prev.map((item) =>
              item.id === id
                ? {
                    ...item,
                    quantity: Math.min(10, item.quantity + quantity)
                  }
                : item
            );
          }

          return [
            ...prev,
            {
              id,
              productId: PRODUCT.id,
              name: PRODUCT.name,
              price: PRODUCT.price,
              colorName,
              image,
              quantity
            }
          ];
        });
      },
      increment(id) {
        setItems((prev) =>
          prev.map((item) =>
            item.id === id
              ? { ...item, quantity: Math.min(10, item.quantity + 1) }
              : item
          )
        );
      },
      decrement(id) {
        setItems((prev) =>
          prev
            .map((item) =>
              item.id === id
                ? { ...item, quantity: Math.max(1, item.quantity - 1) }
                : item
            )
            .filter((item) => item.quantity > 0)
        );
      },
      remove(id) {
        setItems((prev) => prev.filter((item) => item.id !== id));
      },
      clear() {
        setItems([]);
      }
    }),
    [items]
  );

  const totals = useMemo(() => {
    const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
    const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
    return { totalItems, subtotal };
  }, [items]);

  return (
    <CartContext.Provider value={{ ...actions, ...totals }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used inside CartProvider');
  }
  return context;
}
